import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import {
  getEventsAndNotifications,
  markAsRead,
} from "../services/EventsNotifications";
import { UserContext, OrganisationContext, LocalValuesContext } from "./MainLayout";
import {
  timeStringToTimeAMPM,
  getEstimatedTimeElapsed,
  useOutsideAlerter,
} from "../utils/utils";
import Icons from "../utils/components/icons";
import EmptyScreen from "../utils/components/common/EmptyScreen/EmptyScreen";

const filtersObj = {
  All: "All",
  cartridge: "Cartridge",
  connectivity: "Connectivity",
  clo2Value: "PPB Level",
  faults: "Fault",
  deviceEvents: "Device Events",
};

const tooltipText = {
  artridge: "Cartridge",
  connectivity: "Connectivity",
  clo2Value: "High PPB",
  faults: "Fault",
  deviceEvents: "Device Events",
  Notification: "Notification",
};

const notificationTypeIconsh = {
  cartridge: Icons.testTubeIcon,
  connectivity: Icons.connectivityIcon,
  clo2Value: Icons.ppbIcon,
  faults: Icons.faultIcon2,
  deviceEvents: Icons.deviceEventIcon,
};

const EventAndNotification = ({ hidePopup, setListed, buttonRef }) => {

  const { activeFilter, setActiveFilter, isShowUnread, setIsShowUnread, isShowEvent, setIsShowEvent } = useContext(LocalValuesContext);
 
  // const [activeFilter, setActiveFilter] = useState(
  //   sessionStorage.getItem("activeFilter") || "All"
  // );

  // const [isShowUnread, setIsShowUnread] = useState(() => {
  //   const storedValue = sessionStorage.getItem("isShowUnread");
  //   return storedValue !== null ? JSON.parse(storedValue) : true;
  // });
  
  // const [isShowEvent, setIsShowEvent] = useState(
  //   JSON.parse(sessionStorage.getItem("isShowEvent")) || false
  // );

  const [evtsNotificationCounts, setEvtsNotificationCounts] = useState();
  const [selectedNotification, setSelectedNotification] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [evtsNotificationData, setEvtsNotificationData] = useState();
  const [isMarkRead, setIsMarkRead] = useState(false);

  const allNotificationsRead =
    evtsNotificationData && evtsNotificationData.length > 0
      ? evtsNotificationData.every((notification) => notification.IsRead)
      : false;

  const { t } = useTranslation("eventsNotification");
  const wrapperRef = useRef(null);

  const { userCognitoData, appTheme } = useContext(UserContext);
  const { siteData } = useContext(OrganisationContext);
  const navigate = useNavigate();

  const getNotificationList = useCallback(() => {
    setIsLoading(true);
    const param = {
      userId: userCognitoData?.username,
      siteId: siteData?.id,
      eventType: activeFilter,
      duration: 24,
      size: 500,
      pageNo: 1,
      showEvent: isShowEvent,
      timeStamp: "DESC",
    };
    if (isShowUnread) {
      param.showUnread = isShowUnread;
    }
    getEventsAndNotifications(param)
      .then((response) => {
        setEvtsNotificationCounts(response?.data?.CountsAssociatedWithFilter);
        const data = response?.data?.Items || [];
        setIsLoading(false);
        const listedData =
          data.length > 0
            ? data.map((item) => {
                return { ...item, isListed: true };
              })
            : [];
        setEvtsNotificationData(listedData);
      })
      .catch((err) => {
        setIsLoading(false);
        setEvtsNotificationData([]);
      });
  }, [
    activeFilter,
    isShowEvent,
    isShowUnread,
    siteData?.id,
    userCognitoData?.username,
  ]);

  const markAllAsRead = () => {
    const param = {
      userId: userCognitoData.username,
      siteId: siteData.id,
      eventType: activeFilter,
      showEvent: isShowEvent,
      duration: 24,
      isRead: true,
      isListed: true,
    };
    markAsRead(param)
      .then((res) => {
        const updatedData = evtsNotificationData.map((data) => {
          return { ...data, IsRead: true };
        });
        setEvtsNotificationData(updatedData);
        setIsMarkRead(true);
      })
      .catch((err) => {
        // console.log(err);
      });
  };

  const markAllAsListed = useCallback(() => {
    if (userCognitoData.username && siteData.id) {
      // setListed(true);
      const param = {
        userId: userCognitoData.username,
        siteId: siteData.id,
        eventType: "All",
        duration: 24,
        isListed: true,
      };
      markAsRead(param)
        .then((res) => {
          // setListed(true);
        })
        .catch((err) => {
          setListed(false);
          //   console.log(err);
        });
    }
  }, [setListed, siteData.id, userCognitoData.username]);

  const hideEventAndNotification = () => {
    hidePopup(false);
  };

  const markRead = useCallback(
    (id, isRead) => {
      const param = {
        userId: userCognitoData.username,
        siteId: siteData.id,
        isRead: !isRead,
        isListed: true,
        notificationId: [id],
      };
      markAsRead(param)
        .then((res) => {
          const updatedData = evtsNotificationData.map((data) => {
            if (data.PK === id) {
              return { ...data, IsRead: true };
            }
            return data;
          });
          setEvtsNotificationData(updatedData);
        })
        .catch((err) => {
          // console.log(err);
        });
    },
    [evtsNotificationData, userCognitoData.username, siteData]
  );

  const gotoEventPage = () => {
    hidePopup(false);
    navigate("/events-and-notifications");
  };

  const gotoDevicePage = (id) => {
    hidePopup(false);
    navigate(`/device-view/${id}`);
  };

  useOutsideAlerter(wrapperRef, hideEventAndNotification, buttonRef);

  useEffect(() => {
    if (userCognitoData?.username && siteData?.id) {
      getNotificationList();
    }
  }, [
    userCognitoData,
    siteData,
    isShowEvent,
    activeFilter,
    isShowUnread,
    getNotificationList,
  ]);

  useEffect(() => {
    if (userCognitoData?.username && siteData?.id) {
      markAllAsListed();
    }
  }, [userCognitoData, siteData, markAllAsListed]);

  useEffect(() => {
    setIsShowUnread(isShowUnread);
    setActiveFilter(activeFilter);
    setIsShowEvent(isShowEvent);    
  }, [activeFilter, isShowEvent, isShowUnread]);

  return (
    <div className="d-flex flex-column flex-grow" ref={wrapperRef}>
      <div className="d-flex justify-content-between">
        <div className="title d-flex align-items-center">
          <span>{t("event_notification_page_heading")}</span>
          <button onClick={gotoEventPage} id="gotoEventPage">
            {Icons.externalLinkIcon}
          </button>
        </div>
        <div
          className="form-check form-switch"
          onClick={() => setIsShowEvent(!isShowEvent)}
        >
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckChecked"
            checked={isShowEvent}
            onChange={() => {}}
          />
          <label>{t("showEvent")}</label>
        </div>
      </div>
      <div className="filters">
        {Object.keys(filtersObj).map((filter) => {
          return (
            <label
              className={
                filter === activeFilter
                  ? "btn btn-primary active"
                  : "btn btn-primary"
              }
              onClick={() => setActiveFilter(filter)}
              key={filter}
            >
              <input
                type="radio"
                name="options"
                id=""
                className="hide"
                onChange={() => {}}
              />
              <span>{`${filtersObj[filter]}`}</span>
              <span>
                {`(${
                  evtsNotificationCounts
                    ? evtsNotificationCounts["Count_EventType_" + filter]
                    : 0
                })`}
              </span>
            </label>
          );
        })}
      </div>
      <div className="d-flex justify-content-between mb-2">
        <div className="markAsRead d-flex align-items-center">
          <button
            className={
              evtsNotificationData &&
              evtsNotificationData.length > 0 &&
              !allNotificationsRead
                ? ""
                : "disabled"
            }
            onClick={markAllAsRead}
            disabled={
              !evtsNotificationData ||
              evtsNotificationData.length === 0 ||
              allNotificationsRead
            }
          >
            {t("markAllAsRead")}
          </button>
        </div>
        <div
          className="form-check form-switch"
          onClick={() => setIsShowUnread(!isShowUnread)}
        >
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id="flexSwitchCheckChecked"
            checked={isShowUnread}
            onChange={() => {}}
          />
          <label>{t("only_show_unread")}</label>
        </div>
      </div>
      {isLoading && (
        <div className="loadingInner mt-2 mb-2">
          Loading...
          <div className="spinner-border spinner-border-md" role="status"></div>
        </div>
      )}
      {!isLoading &&
        evtsNotificationData.length === 0 &&
        activeFilter === "All" && (
          <EmptyScreen
            img={Icons.noEventIcon}
            small={true}
            title={t("Empty_screen_all_title")}
            desc={t("Empty_screen_all_desc")}
          />
        )}
      {!isLoading &&
        evtsNotificationData.length === 0 &&
        activeFilter !== "All" && (
          <EmptyScreen
            img={Icons.noEventIcon}
            small={true}
            title={t("Empty_screen_title")}
            desc={t("Empty_screen_desc")}
          />
        )}
      {!isLoading && (
        <div className="notificationItemWrapper">
          {evtsNotificationData &&
            evtsNotificationData.map((item, index) => (
              <div
                className={classNames("notificationItem", {
                  isRead: !item?.IsRead,
                })}
                key={`notification_item_${index}`}
                id={`notification_item_${index}`}
                onClick={() => {
                  markRead(item?.PK, item?.IsRead);
                  setSelectedNotification(item?.PK);
                }}
              >
                <div className="notificationType d-flex flex-column">
                  <span
                    className="event-type-icon"
                    title={tooltipText[item.EventType]}
                  >
                    {notificationTypeIconsh[item.EventType]}
                  </span>
                  <span className="mt-2">
                    {Icons.hourIcon}
                    {getEstimatedTimeElapsed(item.EventEstimatedTimeStamp)}
                  </span>
                </div>
                <div
                  className={`notificationDetail ${
                    isMarkRead === true ? "isMarkRead" : ""
                  }`}
                >
                  <p>
                    <span
                      className={
                        selectedNotification === item?.PK
                          ? "title mb-2 pointer word-break hyperlink"
                          : "title mb-2 pointer word-break"
                      }
                      onClick={() => {
                        selectedNotification === item?.PK &&
                          gotoDevicePage(item?.DeviceUUID);
                      }}
                      title={
                        selectedNotification === item?.PK
                          ? "Go to Device View"
                          : ""
                      }
                    >
                      {item?.DeviceName}
                    </span>
                  </p>
                  <span className="status">{item?.EventName}</span>
                  {selectedNotification === item?.PK && (
                    <>
                      <span className="desc mt-2 mb-2">
                        {item?.NotificationBody}
                      </span>
                      <span className="dateTime">
                        {timeStringToTimeAMPM(item?.EventEstimatedTimeStamp)}
                      </span>
                    </>
                  )}
                </div>
                {item?.IsNotification && (
                  <div
                    className="isNotification"
                    title={tooltipText.Notification}
                  >
                    {appTheme === "dark" ? (
                      <img
                        src={
                          item?.IsRead
                            ? require("../assets/images/icons/dark_notification.png")
                            : require("../assets/images/icons/notification.png")
                        }
                        className="img-fluid"
                        alt="notification"
                      />
                    ) : (
                      <img
                        src={require("../assets/images/icons/notification.png")}
                        className="img-fluid"
                        alt="notification"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default EventAndNotification;
